import { ChangeState, change, createChangeState } from "@greeter/changes";
import {
  Bundle,
  BundleLine,
  Category,
  DiscountStrategy,
  emptyBundle,
  ImageAsset,
  Integration,
} from "@greeter/core";
import { notImplemented } from "@greeter/xstate-util";
import {
  assertEvent,
  assign,
  emit,
  enqueueActions,
  raise,
  setup,
} from "xstate";
import { SpecialPricePeriodChangeset } from "./SpecialPricePeriodEditor";
import * as uuid from "uuid";
import { ActivePeriodsChangeset } from "./ActivePeriodsEditor";
import { EditBundleApiRequirements } from "./EditBundlePage";
import { assert } from "@greeter/assert";
import { FeatureFlags } from "@greeter/core";
import { Log } from "@greeter/log";

type ValidationError =
  | { type: "missing-title" }
  | { type: "missing-price" }
  | { type: "missing-cover" }
  | { type: "missing-lines" }
  | { type: "discount-price-larger-than-price" }
  | { type: "invalid-quantity"; data: { bundleLineId: string } };

export function hasError(
  validationErrors: Array<ValidationError>,
  err: ValidationError["type"],
  predicate?: (err: ValidationError) => boolean
) {
  return !!validationErrors.find((e) => {
    return e.type === err && (predicate ? predicate(e) : true);
  });
}

function validateBundle(bundle: Bundle, validateQuantity?: boolean) {
  console.group("[validateBundle]");

  const validationErrors: Array<ValidationError> = [];

  // if (bundle.discountPrice && bundle.discountPrice >= bundle.price) {
  //   validationErrors.push("discount-price-larger-than-price");
  // }

  if (bundle.price <= 0) {
    validationErrors.push({ type: "missing-price" });
  }

  if (!bundle.lines || bundle.lines.length === 0) {
    validationErrors.push({ type: "missing-lines" });
  }

  if (bundle.title.trim() === "") {
    validationErrors.push({ type: "missing-title" });
  }

  if (!bundle.coverAsset) {
    validationErrors.push({ type: "missing-cover" });
  }

  console.log("validationErrors (before)", {
    validationErrors,
    validateQuantity,
  });
  if (!validateQuantity) {
    console.groupEnd();
    return validationErrors;
  }

  for (let line of bundle.lines) {
    const quantity = line.quantity;
    let totalQuantity = line.variants.reduce(
      (acc, curr) => acc + (curr.quantity ?? 0),
      0
    );

    if (totalQuantity !== quantity) {
      validationErrors.push({
        type: "invalid-quantity",
        data: { bundleLineId: line.id },
      });
    }
  }

  console.log("validationErrors (after)", {
    validationErrors,
    validateQuantity,
  });
  console.groupEnd();
  return validationErrors;
}

type EditBundleMachineContext = {
  changes: ChangeState<Bundle, BundleChange>;
  bundleId?: string;
  pickingForBundleLineId?: string;
  categories?: Array<Category>;
  fileToUpload?: File;

  activePeriodsChangeset?: ActivePeriodsChangeset;
  specialPeriodChangeset?: SpecialPricePeriodChangeset;

  validationErrors: Array<ValidationError>;

  // OnlinePOS integration
  onlinePOSIntegration?: Integration;
  onlinePOSProductId?: number;

  featureFlags?: FeatureFlags;
};

type SetTitleChange = {
  type: "setTitle";
  data: string;
};

type SetCoverChange = {
  type: "setCover";
  data: ImageAsset;
};

type SetDescriptionChange = {
  type: "setDescription";
  data: string;
};

type SetPriceChange = {
  type: "setPrice";
  data: number;
};

type SetDiscountFixedChange = {
  type: "setDiscountFixed";
  data?: number;
};

type SetDiscountPercentageChange = {
  type: "setDiscountPercentage";
  data?: number;
};

type SetActiveChange = {
  type: "setIsPublic";
  data: boolean;
};

type SetAdditionalPriceChange = {
  type: "setAdditionalPrice";
  data: { variationId: string; additionalPrice: { amount: number } };
};

type SetQuantityChange = {
  type: "setQuantity";
  data: { variationId: string; quantity: number };
};

type SetBundleLineNameChange = {
  type: "setBundleLineName";
  data: { bundleLineId: string; name: string };
};

type ReorderBundleLine = {
  type: "reorderBundleLine";
  data: { bundleLineId: string; bundleLineIdToInsertAt: string };
};

type SetBundleLineQuantityChange = {
  type: "setBundleLineQuantity";
  data: { bundleLineId: string; quantity: number };
};

type PickedProduct = {
  id: string;
  categoryName: string;
  name: string;
};
type SetBundleLineVariantsChange = {
  type: "setBundleLineVariants";
  data: { bundleLineId: string; products: Array<PickedProduct> };
};

type RemoveBundleLineChange = {
  type: "removeBundleLine";
  data: string;
};

type RemoveBundleLineVariantChange = {
  type: "removeBundleLineVariant";
  data: string;
};

type AddBundleLineVariantChange = {
  type: "addBundleLineVariant";
  data: { bundleLineId?: string; products: Array<PickedProduct> };
};

type ReorderBundleLineVariantChange = {
  type: "reorderBundleLineVariant";
  data: { bundleLineVariantId: string; bundleLineVariantIdToInsertAt: string };
};

type SetDiscountStrategy = {
  type: "setDiscountStrategy";
  data: DiscountStrategy;
};

export type BundleChange =
  | SetTitleChange
  | SetActiveChange
  | SetPriceChange
  | SetCoverChange
  | SetDiscountFixedChange
  | SetDiscountPercentageChange
  | SetAdditionalPriceChange
  | SetDescriptionChange
  | SetBundleLineNameChange
  | SetBundleLineQuantityChange
  | SetQuantityChange
  | SetDiscountStrategy
  | ReorderBundleLine
  | SetBundleLineVariantsChange
  | AddBundleLineVariantChange
  | ReorderBundleLineVariantChange
  | RemoveBundleLineVariantChange
  | RemoveBundleLineChange;

function apply(bundle: Bundle, change: BundleChange) {
  console.group("[apply]");
  console.debug(change.type, change.data);

  switch (change.type) {
    case "setTitle": {
      bundle.title = change.data;
      break;
    }

    case "setIsPublic": {
      bundle.isPublic = change.data;
      break;
    }

    case "setDescription": {
      bundle.description = change.data;
      break;
    }

    case "setPrice": {
      bundle.price = change.data;
      break;
    }

    case "setCover": {
      bundle.coverAssetId = change.data.id;
      bundle.coverUrl = change.data.uri;
      bundle.coverAsset = change.data;
      break;
    }

    case "setAdditionalPrice": {
      let found = false;
      for (const line of bundle.lines) {
        if (found) {
          break;
        }
        for (const variation of line.variants) {
          if (variation.id === change.data.variationId) {
            variation.additionalPrice = change.data.additionalPrice.amount;
            found = true;
            break;
          }
        }
      }
      break;
    }

    case "setQuantity": {
      let foundLine: BundleLine | undefined = undefined;
      for (const line of bundle.lines) {
        for (const variation of line.variants) {
          if (variation.id === change.data.variationId) {
            foundLine = line;
            break;
          }
        }
      }

      if (!foundLine) {
        console.warn("could not find line when running 'setQuantity'");
        break;
      }

      let totalQuantity = 0;
      for (const variation of foundLine?.variants) {
        totalQuantity += variation.quantity ?? 0;
      }

      for (const variant of foundLine.variants) {
        if (variant.id === change.data.variationId) {
          const quantity = Math.max(change.data.quantity, 0);
          const diff = quantity - (variant.quantity ?? 0);
          const total = totalQuantity + diff;
          if (
            total <= foundLine.quantity ||
            (total > foundLine.quantity && diff < 0)
          ) {
            variant.quantity = quantity;
          }
          break;
        }
      }
      break;
    }

    case "setDiscountPercentage": {
      bundle.discountPercentage = change.data ?? 0;
      break;
    }

    case "setDiscountFixed": {
      bundle.discountFixed = change.data ?? 0;
      break;
    }

    case "setDiscountStrategy": {
      bundle.discountStrategy = change.data;
      break;
    }

    case "setBundleLineQuantity": {
      const line = bundle.lines.find((l) => l.id === change.data.bundleLineId);
      if (line) {
        line.quantity = Math.max(change.data.quantity, 1);
        if (line.variants.length === 1) {
          const variant = line.variants[0];
          variant.quantity = line.quantity;
        }
      }
      break;
    }

    case "setBundleLineName": {
      assert(
        change.data.bundleLineId &&
          typeof change.data.bundleLineId === "string",
        "change.data.bundleLine should be a string"
      );
      assert(
        change.data.name && typeof change.data.name === "string",
        "change.data.name has to be a string and contain something"
      );
      const line = bundle.lines.find((l) => l.id === change.data.bundleLineId);
      assert(
        line,
        "could not find line, which we should be able to, why oh are you not there?"
      );

      line.name = change.data.name;
      break;
    }

    case "addBundleLineVariant": {
      if (!change.data.bundleLineId) {
        let name = change.data.products?.at(0)?.categoryName ?? "";

        if (change.data.products.length === 1)
          name = change.data.products?.at(0)?.name ?? "";

        const line: BundleLine = {
          id: uuid.v7(),
          quantity: 1,
          name: name,
          variants: [],
          priority: bundle.lines.length,
        };

        for (const p of change.data.products) {
          line.variants.push({
            id: uuid.v7(),
            productId: p.id,
            name: p.name,
            additionalPrice: 0,
            priority: line.variants.length,
            quantity: change.data.products.length > 1 ? 0 : 1,
            salesPrice: 0,
          });
        }
        bundle.lines.push(line);
        break;
      }

      const line = bundle.lines.find((l) => l.id === change.data.bundleLineId);
      assert(line, "could not find the line, which it should be here");
      const wasSingleLine = line.variants.length === 1;
      const nameSet = line.variants.reduce(
        (acc, curr) => acc.add(curr.name),
        new Set()
      );
      const hasOriginalName = line && nameSet.has(line.name);
      if (wasSingleLine && hasOriginalName) {
        line.name = change.data.products?.at(0)?.categoryName ?? "";
      }

      console.debug("[BundleMachine][apply]", {
        line,
        wasSingleLine,
        hasOriginalName,
        nameSet,
      });

      if (line) {
        for (const p of change.data.products) {
          line.variants.push({
            id: uuid.v7(),
            productId: p.id,
            name: p.name,
            additionalPrice: 0,
            quantity: 0,
            priority: line.variants.length,
            salesPrice: 0,
          });
        }
      }
      break;
    }

    case "reorderBundleLine": {
      const index = bundle.lines.findIndex(
        (l) => l.id === change.data.bundleLineId
      );
      if (index === -1) break;
      const swapIndex = bundle.lines.findIndex(
        (l) => l.id === change.data.bundleLineIdToInsertAt
      );
      if (swapIndex === -1) break;
      const item = bundle.lines[index];
      bundle.lines.splice(index, 1);
      bundle.lines.splice(swapIndex, 0, item);

      const lines = bundle.lines;
      for (let i = 0, l = lines.length; i < l; i++) {
        lines[i].priority = i;
      }
      break;
    }

    case "setBundleLineVariants": {
      const line = bundle.lines.find((l) => l.id === change.data.bundleLineId);

      if (line) {
        const newProductIdsSet = new Map<string, PickedProduct>(
          change.data.products.map((p) => [p.id, p])
        );
        const currentVariants = new Map<string, PickedProduct>(
          line.variants.map((v) => [
            v.productId,
            { id: v.productId, name: v.name, categoryName: line.name },
          ])
        );

        const removed = new Map<string, PickedProduct>(
          line.variants
            .filter((v) => !newProductIdsSet.get(v.productId))
            .map((v) => [
              v.productId,
              { id: v.productId, name: v.name, categoryName: line.name },
            ])
        );
        const added: Map<string, PickedProduct> = new Map();

        for (let i = 0, l = change.data.products.length; i < l; i++) {
          const picked = change.data.products[i];

          if (!currentVariants.get(picked.id)) {
            added.set(picked.id, picked);
          }
        }
        line.variants = line.variants.filter((v) => !removed.get(v.productId));
        line.variants = line.variants.concat(
          [...added.values()].map((pickedProduct) => {
            return {
              id: uuid.v7(),
              name: pickedProduct.name,
              productId: pickedProduct.id,
              additionalPrice: 0,
              salesPrice: 0,
              priority: line.variants.length,
            };
          })
        );
      }
      break;
    }

    case "reorderBundleLineVariant": {
      const line = bundle.lines.find((l) =>
        l.variants.find((v) => v.id === change.data.bundleLineVariantId)
      );
      if (!line) break;

      const index = line.variants.findIndex(
        (v) => v.id === change.data.bundleLineVariantId
      );
      if (index === -1) break;

      const swapIndex = line.variants.findIndex(
        (v) => v.id === change.data.bundleLineVariantIdToInsertAt
      );
      if (swapIndex === -1) break;

      const item = line.variants[index];
      line.variants.splice(index, 1);
      line.variants.splice(swapIndex, 0, item);

      const variants = line.variants;
      for (let i = 0, l = variants.length; i < l; i++) {
        variants[i].priority = i;
      }

      break;
    }

    case "removeBundleLine":
      bundle.lines = bundle.lines.filter((l) => l.id !== change.data);
      break;

    case "removeBundleLineVariant":
      bundle.lines = bundle.lines.map((l) => {
        l.variants = l.variants.filter((v) => v.id !== change.data);
        if (l.variants.length === 1) {
          l.name = l.variants.at(0)?.name ?? l.name;
        }
        return l;
      });
      break;
  }
  console.groupEnd();
}

type ValidateEvent = {
  type: "validate";
};

type ChangeEvent = {
  type: "change";
  data: BundleChange;
};

type NextEvent = {
  type: "next";
};

type FetchEvent = {
  type: "fetch";
};

type SaveEvent = {
  type: "save";
};

type OpenProductPickerEvent = {
  type: "openProductPicker";
  data?: { bundleLineId: string };
};

type PickProductsEvent = {
  type: "pickProducts";
  data: Array<PickedProduct>;
};

type ResetEvent = {
  type: "reset";
};

type BackEvent = {
  type: "back";
};

type UploadAssetEvent = {
  type: "uploadAsset";
  data: File;
};

type StartUploadEvent = {
  type: "startUpload";
};

type FetchBundleEvent = {
  type: "fetchBundle";
};

type SetSpecialPricePeriodChangesetEvent = {
  type: "setSpecialPricePeriodChangeset";
  data: SpecialPricePeriodChangeset;
};

type SetActivePeriodsChangesetEvent = {
  type: "setActivePeriodsChangeset";
  data: ActivePeriodsChangeset;
};

type DeleteEvent = { type: "delete" };

type SetOnlinePOSProductId = {
  type: "setOnlinePOSProductId";
  data: { onlinePOSProductId: number | null };
};

type SetFeatureFlags = {
  type: "setFeatureFlags";
  data: FeatureFlags;
};

type EditBundleEvents =
  | { type: "idle" }
  | ChangeEvent
  | NextEvent
  | FetchEvent
  | FetchBundleEvent
  | SaveEvent
  | DeleteEvent
  | OpenProductPickerEvent
  | PickProductsEvent
  | ValidateEvent
  | ResetEvent
  | BackEvent
  | UploadAssetEvent
  | StartUploadEvent
  | SetSpecialPricePeriodChangesetEvent
  | SetActivePeriodsChangesetEvent
  | SetOnlinePOSProductId
  | SetFeatureFlags
  | OnlinePOSEvents;

type OnlinePOSEvents =
  | {
      type: "saveOnlinePOSProductConnection";
      data: { onlinePOSProductId?: number };
    }
  | { type: "fetchOnlinePOSIntegration" }
  | { type: "fetchOnlinePOSProductionConnection" };

type OnlinePOSEmitted =
  | {
      type: "failedToSaveOnlinePOSProductConnection";
      error: Error;
    }
  | { type: "failedToFetchOnlinePOSProductConnection"; error: Error };

export type EditBundleEmittedEvents =
  | { type: "editing" }
  | { type: "failedToFetchBundle"; error: Error }
  | { type: "savingBundle" }
  | { type: "savedBundle" }
  | { type: "deletingBundle" }
  | { type: "deletedBundle" }
  | { type: "failedToDeleteBundle"; error: Error }
  | { type: "savingBundleFailed" }
  | { type: "validationFailed" }
  | { type: "fetchedActivePeriods" }
  | { type: "uploadingAsset" }
  | { type: "uploadedAsset" }
  | { type: "fetchedBundle" }
  | { type: "failedToSaveSpecialPricePeriods"; error: Error }
  | { type: "failedToDeleteSpecialPricePeriods"; error: Error }
  | OnlinePOSEmitted;

export const editBundleMachine = setup({
  types: {} as {
    input: {
      bundleId: string | undefined;
      featureFlags?: FeatureFlags;
    };
    context: EditBundleMachineContext;
    events: EditBundleEvents;
    emitted: EditBundleEmittedEvents;
  },
  guards: {
    hasOnlinePOSIntegration({ context }) {
      return context.onlinePOSIntegration?.enabled ?? false;
    },

    hasSpecialPricePeriodsToDelete({ context }) {
      return (
        !!context.specialPeriodChangeset &&
        context.specialPeriodChangeset.toDelete.length > 0
      );
    },

    hasSpecialPricePeriodsToSave({ context }) {
      return (
        !!context.specialPeriodChangeset &&
        context.specialPeriodChangeset.toSave.length > 0
      );
    },

    hasActivePeriodsToSave({ context }) {
      console.debug(`[hasActivePeriodsTosave] active periods to save`, {
        activePeriodsChangeset: context.activePeriodsChangeset,
        toSave: context?.activePeriodsChangeset?.toSave,
      });
      return (
        !!context.activePeriodsChangeset &&
        context.activePeriodsChangeset?.toSave.length > 0
      );
    },

    hasActivePeriodsToDelete({ context }) {
      console.debug(
        `[hasActivePeriodsToDelete] active periods to save`,
        context.activePeriodsChangeset,
        context?.activePeriodsChangeset?.toDelete
      );
      return (
        !!context.activePeriodsChangeset &&
        context.activePeriodsChangeset?.toDelete.length > 0
      );
    },
  },
  actors: {
    fetchOnlinePOSIntegration: notImplemented<() => Promise<Integration>>(),
    fetchOnlinePOSProductConnection:
      notImplemented<
        EditBundleApiRequirements["fetchOnlinePOSProductConnection"]
      >(),
    saveOnlinePOSProductConnection:
      notImplemented<
        EditBundleApiRequirements["saveOnlinePOSProductConnection"]
      >(),
    fetchBundle: notImplemented<EditBundleApiRequirements["fetchBundle"]>(),
    deleteBundle: notImplemented<EditBundleApiRequirements["deleteBundle"]>(),
    saveBundle: notImplemented<EditBundleApiRequirements["saveBundle"]>(),
    uploadAsset: notImplemented<EditBundleApiRequirements["uploadAsset"]>(),
    saveActivePeriods:
      notImplemented<EditBundleApiRequirements["saveActivePeriods"]>(),
    deleteActivePeriods:
      notImplemented<EditBundleApiRequirements["deleteActivePeriods"]>(),
    saveSpecialPricePeriods:
      notImplemented<EditBundleApiRequirements["saveSpecialPricePeriods"]>(),
    deleteSpecialPricePeriods:
      notImplemented<EditBundleApiRequirements["deleteSpecialPricePeriods"]>(),
  },
}).createMachine({
  context({ input }) {
    return {
      activePeriodsChanges: createChangeState({ toSave: [] }),
      changes: createChangeState(emptyBundle()),
      categories: [],
      bundleId: input.bundleId,
      featureFlags: new Map(input.featureFlags ?? []),
      validationErrors: [],
    };
  },
  type: "parallel",
  on: {
    setFeatureFlags: {
      actions: assign({ featureFlags: ({ event }) => event.data }),
    },
  },
  states: {
    onlinePOSIntegration: {
      initial: "fetching",
      states: {
        idle: {
          on: {
            fetchOnlinePOSIntegration: "fetching",
          },
        },
        fetching: {
          invoke: {
            src: "fetchOnlinePOSIntegration",
            input: () => [],
            onError: "idle",
            onDone: {
              actions: [
                assign(({ event }) => ({
                  onlinePOSIntegration: event.output,
                })),
                raise({ type: "fetchOnlinePOSProductionConnection" }),
              ],
            },
          },
        },
      },
    },

    onlinePOSProductConnection: {
      initial: "idle",
      states: {
        idle: {
          on: {
            saveOnlinePOSProductConnection: "saving",
            fetchOnlinePOSProductionConnection: {
              target: "fetching",
              guard: "hasOnlinePOSIntegration",
            },
          },
        },
        fetching: {
          invoke: {
            src: "fetchOnlinePOSProductConnection",
            input({ context }) {
              return [context.bundleId ?? ""];
            },
            onDone: {
              target: "idle",
              actions: [
                assign(({ event }) => ({
                  onlinePOSProductId: event.output.onlinePOSProductId,
                })),
              ],
            },
            onError: {
              target: "idle",
              actions: emit(({ event }) => ({
                type: "failedToFetchOnlinePOSProductConnection",
                error: event.error as Error,
              })),
            },
          },
        },
        saving: {
          invoke: {
            src: "saveOnlinePOSProductConnection",
            input({ context, event }) {
              assertEvent(event, "saveOnlinePOSProductConnection");

              return [
                {
                  productId: context.bundleId!,
                  onlinePOSProductId: context.onlinePOSProductId ?? null,
                },
              ];
            },
            onDone: "idle",
            onError: "idle",
          },
        },
      },
    },

    assets: {
      initial: "idle",
      states: {
        idle: {
          on: {
            uploadAsset: {
              actions: [
                assign({ fileToUpload: ({ event }) => event.data }),
                raise({ type: "startUpload" }),
              ],
            },
            startUpload: {
              target: "uploading",
              guard: ({ context }) => {
                return !!context.fileToUpload;
              },
            },
          },
        },
        uploading: {
          entry: emit({ type: "uploadingAsset" }),
          invoke: {
            src: "uploadAsset",
            input: ({ context }) => [{ data: context.fileToUpload! }],
            onDone: {
              target: "idle",
              actions: [
                raise(({ event }) => ({
                  type: "change",
                  data: { type: "setCover", data: event.output },
                })),
                emit({ type: "uploadedAsset" }),
              ],
            },
          },
        },
      },
    },

    fetchingBundle: {
      initial: "init",
      states: {
        init: {
          entry: enqueueActions(({ context, enqueue }) => {
            if (!!context.bundleId) {
              enqueue.raise({ type: "fetchBundle" });
            } else {
              enqueue.raise({ type: "idle" });
            }
          }),
          on: {
            idle: "idle",
            fetchBundle: "fetching",
            fetch: "fetching",
          },
        },
        idle: {
          on: {
            fetchBundle: "fetching",
            fetch: "fetching",
          },
        },
        failed: {
          on: {
            fetchBundle: "fetching",
            fetch: "fetching",
          },
        },
        fetching: {
          invoke: {
            src: "fetchBundle",
            input: ({ context }) => {
              return [context.bundleId!];
            },
            onError: {
              target: "failed",
              actions: emit(({ event }) => ({
                type: "failedToFetchBundle",
                error: event.error as Error,
              })),
            },
            onDone: {
              target: "idle",
              actions: [
                assign(({ event }) => {
                  if (event.output) {
                    return { changes: createChangeState(event.output) };
                  }
                  return {};
                }),
              ],
            },
          },
          exit: emit({ type: "fetchedBundle" }),
        },
      },
    },

    deleteBundle: {
      initial: "idle",
      states: {
        idle: { on: { delete: "deleting" } },
        deleting: {
          entry: emit({ type: "deletingBundle" }),
          invoke: {
            src: "deleteBundle",
            input({ context }) {
              return [context.changes.value.id];
            },
            onDone: [
              { target: "idle", actions: [emit({ type: "deletedBundle" })] },
            ],
            onError: {
              target: "idle",
              actions: [
                emit(({ event }) => ({
                  type: "failedToDeleteBundle",
                  error: event.error as Error,
                })),
              ],
            },
          },
        },
      },
    },

    saveBundle: {
      initial: "idle",
      states: {
        idle: { on: { validate: "validating" } },
        invalid: {
          entry: emit({ type: "validationFailed" }),
          on: { validate: "validating" },
        },
        validating: {
          entry: raise({ type: "validate" }),
          on: {
            validate: {
              actions: [
                assign(({ context }) => {
                  return {
                    validationErrors: validateBundle(
                      context.changes.value,
                      true
                    ),
                  };
                }),
              ],
            },
            save: [
              {
                target: "saving",
                guard: ({ context }) => {
                  return (
                    !context.validationErrors ||
                    context.validationErrors.length === 0
                  );
                },
              },
              { target: "invalid" },
            ],
          },
        },
        saving: {
          entry: emit({ type: "savingBundle" }),
          invoke: {
            src: "saveBundle",
            input: ({ context }) => [context.changes.value!],
            onDone: [
              {
                target: "deletingActivePeriods",
                guard: "hasActivePeriodsToDelete",
                actions: assign({ bundleId: ({ event }) => event.output.id }),
              },
              {
                target: "savingActivePeriods",
                guard: "hasActivePeriodsToSave",
                actions: assign({ bundleId: ({ event }) => event.output.id }),
              },
              {
                target: "deletingSpecialPricePeriods",
                guard: "hasSpecialPricePeriodsToDelete",
                actions: assign({ bundleId: ({ event }) => event.output.id }),
              },
              {
                target: "savingSpecialPricePeriods",
                guard: "hasSpecialPricePeriodsToSave",
                actions: assign({ bundleId: ({ event }) => event.output.id }),
              },
              {
                target: "savingOnlinePOSProductConnection",
                guard: "hasOnlinePOSIntegration",
                actions: assign({ bundleId: ({ event }) => event.output.id }),
              },
              {
                target: "idle",
                actions: [
                  emit({ type: "savedBundle" }),
                  assign({ bundleId: ({ event }) => event.output.id }),
                ],
              },
            ],
            onError: {
              target: "idle",
              actions: [
                () => Log.error("FAILED to save bundle"),
                emit({ type: "savingBundleFailed" }),
              ],
            },
          },
        },
        deletingActivePeriods: {
          invoke: {
            src: "deleteActivePeriods",
            input({ context }) {
              return [context.activePeriodsChangeset?.toDelete ?? []];
            },
            onDone: [
              {
                target: "savingActivePeriods",
                guard: "hasActivePeriodsToSave",
              },
              {
                target: "deletingSpecialPricePeriods",
                guard: "hasSpecialPricePeriodsToDelete",
              },
              {
                target: "savingSpecialPricePeriods",
                guard: "hasSpecialPricePeriodsToDelete",
              },
              {
                target: "savingOnlinePOSProductConnection",
                guard: "hasOnlinePOSIntegration",
              },
              { target: "idle", actions: emit({ type: "savedBundle" }) },
            ],
          },
        },
        savingActivePeriods: {
          invoke: {
            src: "saveActivePeriods",
            input({ context }) {
              return [context.activePeriodsChangeset?.toSave ?? []];
            },
            onDone: [
              {
                target: "deletingSpecialPricePeriods",
                guard: "hasSpecialPricePeriodsToDelete",
              },
              {
                target: "savingSpecialPricePeriods",
                guard: "hasSpecialPricePeriodsToDelete",
              },
              {
                target: "savingOnlinePOSProductConnection",
                guard: "hasOnlinePOSIntegration",
              },
              { target: "idle", actions: emit({ type: "savedBundle" }) },
            ],
          },
        },
        deletingSpecialPricePeriods: {
          invoke: {
            src: "deleteSpecialPricePeriods",
            input({ context }) {
              return [context.specialPeriodChangeset!.toDelete];
            },
            onDone: [
              {
                target: "savingSpecialPricePeriods",
                guard: "hasSpecialPricePeriodsToSave",
              },
              {
                target: "savingOnlinePOSProductConnection",
                guard: "hasOnlinePOSIntegration",
              },
              { target: "idle", actions: [emit({ type: "savedBundle" })] },
            ],
            onError: {
              actions: emit(({ event }) => ({
                type: "failedToDeleteSpecialPricePeriods",
                error: event.error as Error,
              })),
            },
          },
        },
        savingSpecialPricePeriods: {
          invoke: {
            src: "saveSpecialPricePeriods",
            input({ context }) {
              return [context.specialPeriodChangeset!.toSave];
            },
            onDone: [
              {
                target: "savingOnlinePOSProductConnection",
                guard: "hasOnlinePOSIntegration",
              },
              { target: "idle", actions: [emit({ type: "savedBundle" })] },
            ],
            onError: {
              actions: emit(({ event }) => ({
                type: "failedToSaveSpecialPricePeriods",
                error: event.error as Error,
              })),
            },
          },
        },
        savingOnlinePOSProductConnection: {
          invoke: {
            src: "saveOnlinePOSProductConnection",
            input({ context }) {
              assert(
                context.bundleId,
                "context.bundleId is required to save onlinePOS product connection"
              );
              return [
                {
                  productId: context.bundleId,
                  onlinePOSProductId: context.onlinePOSProductId ?? null,
                },
              ];
            },
            onError: {
              actions: emit(({ event }) => ({
                type: "failedToSaveOnlinePOSProductConnection",
                error: event.error as Error,
              })),
            },
            onDone: {
              target: "idle",
              actions: [emit({ type: "savedBundle" })],
            },
          },
        },
      },
    },

    ui: {
      initial: "editing",
      on: {
        setOnlinePOSProductId: {
          actions: assign(({ event }) => ({
            onlinePOSProductId: event.data.onlinePOSProductId ?? undefined,
          })),
        },
        setSpecialPricePeriodChangeset: {
          actions: assign(({ event }) => ({
            specialPeriodChangeset: event.data,
          })),
        },
        setActivePeriodsChangeset: {
          actions: [
            assign(({ event }) => ({
              activePeriodsChangeset: event.data,
            })),
          ],
        },
        reset: {
          actions: assign(() => ({
            changes: createChangeState(emptyBundle()),
          })),
        },
        change: {
          actions: assign(({ event, context }) => {
            const result = change(context.changes, apply, event.data);
            return { changes: result };
          }),
        },
      },
      states: {
        editing: {
          entry: emit({ type: "editing" }),
          on: {
            openProductPicker: {
              target: "pickingProducts",
              actions: assign(({ event }) => ({
                pickingForBundleLineId: event.data?.bundleLineId,
              })),
            },
          },
        },

        pickingProducts: {
          on: {
            pickProducts: {},
            back: "editing",
          },
        },
      },
    },
  },
});
